import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import { theme } from '../../../styles/theme';
import { isIndividualUserType, isOrganizationDropdownForIndividual, normalizeUserType } from '../../../Utils/userType';
import { convertToVwWeb } from '../../../Utils/responsive';
import dropdownIcon from '../../../assets/Icon/project/inputbox_icon.png';
import calendarIcon from '../../../assets/Icon/project/calendar_icon.png';
import hugeIcon from '../../../assets/Icon/project/huge_icon.png';
import searchIcon from '../../../assets/Icon/project/serch_icon.png';
import buildingIcon from '../../../assets/Icon/project/building_icon.png';
import bankIcon from '../../../assets/Icon/project/bank_icon.png';
import securityIcon from '../../../assets/Icon/project/security_icon.png';
import searchColorIcon from '../../../assets/Icon/project/serch_color_icon.png';
import buildingColorIcon from '../../../assets/Icon/project/building_color_icon.png';
import bankColorIcon from '../../../assets/Icon/project/bank_color_icon.png';
import securityColorIcon from '../../../assets/Icon/project/security_color_icon.png';
import certificateIcon from '../../../assets/Icon/project/certificate.png';
import lawIcon from '../../../assets/Icon/project/law_icon.png';
import deleteIcon from '../../../assets/Icon/project/delete_icno.png';
import photoIcon from '../../../assets/Icon/project/photo_photo.png';
import defaultProfileIcon from '../../../assets/Icon/project/default-profile.png';
import profileColorIcon from '../../../assets/Icon/project/iconamoon_color_profile-fill.png';
import profileCertificateIcon from '../../../assets/Icon/project/profile_certificate.png';
import profileCertificateFilledIcon from '../../../assets/Icon/project/profile_certificate_filled.png';
import { useMyAccount } from '../useMyAccount';
import { useAppSelector } from '../../../store/hooks';
import { selectAuthUser } from '../../../Module/Auth/Presentation/authSelectors';
import { CancelSaveButtons, BirthDatePicker } from '../../../components/common';
import DSModal from '../../../components/designSystem/DSModal';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../../../routes/paths';
import { getCountryOptions, getCountryLabel } from '../../../Utils/countryUtils';
import { getOrganizationDisplayName, getOrganizationOptions, ORGANIZATION_ETC_CODE } from '../../../Utils/organizationOptions';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { formatUsStateLabel, US_STATES } from '../../../Utils/usStates';

// --- Styled Components ---

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(20)};
  align-items: center;
  padding-top: 24px;
  padding-bottom: ${convertToVwWeb(100)};
  min-height: 100vh;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(20)};
  width: 100%;
  max-width: ${convertToVwWeb(892)};
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const StepIndicatorWrapper = styled.div`
  background-color: ${theme.colors.primaryBlue[10]};
  border: 1.6px solid ${theme.colors.primaryBlue[40]};
  border-radius: ${convertToVwWeb(10)};
  padding: ${convertToVwWeb(20)};
  width: 100%;
`;

const StepHintBlackBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(4)};
  width: 100%;
`;

const StepHintStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StepText = styled.p`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

/** 스텝 제목 — 프로그레스 바 바로 위 (Contents/Medium20, primary blue) */
const StepSubText = styled.p`
  font-family: Pretendard, sans-serif;
  ${theme.typography.contents.medium20};
  color: var(--primary-blue-40, ${theme.colors.primaryBlue[40]});
  margin: 0;
  margin-top: ${convertToVwWeb(4)};
`;

const StepProgressRow = styled.div`
  margin-top: ${convertToVwWeb(8)};
`;

const StepList = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: fit-content;
  height: ${convertToVwWeb(30)};
`;

const StepLine = styled.div`
  position: absolute;
  top: 50%;
  left: ${convertToVwWeb(15)};
  width: ${convertToVwWeb(193)};
  height: 1px;
  background-color: #e5e5e5;
  z-index: 0;
  transform: translateY(-50%);
`;

const StepCircle = styled.div<{ active?: boolean }>`
  width: ${props => (props.active ? convertToVwWeb(30) : convertToVwWeb(20))};
  height: ${props => (props.active ? convertToVwWeb(30) : convertToVwWeb(20))};
  border-radius: 50%;
  background-color: ${props => (props.active ? theme.colors.primaryBlue[40] : '#e5e5e5')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: white;
  margin-right: ${convertToVwWeb(44)};
  
  &:last-child {
    margin-right: 0;
  }

  p {
    ${theme.typography.contents.regular20};
    font-size: ${props => (props.active ? convertToVwWeb(20) : convertToVwWeb(12))};
    margin: 0;
    line-height: 1;
  }
`;

const TabMenu = styled.div`
  display: flex;
  background-color: white;
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwWeb(12)} ${convertToVwWeb(12)} 0 0;
  width: 100%;
  min-width: 0;
  padding: 0 clamp(12px, 2.2vw, 32px);
  gap: clamp(12px, 3.3vw, 48px);
  position: relative;
  z-index: 1;
`;

const TabItem = styled.div<{ active?: boolean }>`
  height: clamp(44px, 4vw, 58px);
  display: flex;
  align-items: center;
  gap: clamp(4px, 0.55vw, 8px);
  cursor: pointer;
  border-bottom: ${props => (props.active ? `4px solid ${theme.colors.primaryBlue[40]}` : 'none')};
  margin-bottom: -1px;
  flex-shrink: 0;

  img {
    width: clamp(20px, 2.2vw, 32px);
    height: clamp(20px, 2.2vw, 32px);
    object-fit: contain;
  }

  p {
    ${theme.typography.contents.medium20};
    font-size: clamp(12px, 1.4vw, 20px);
    color: ${props => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[50])};
    margin: 0;
    white-space: nowrap;
  }
`;

const SectionContainer = styled.div`
  border-radius: ${convertToVwWeb(12)} ${convertToVwWeb(12)} 0 0;
  border: 1px solid ${theme.colors.grayScale[20]};
  border-bottom: none;
  background: ${theme.colors.grayScale[0]};
  padding: ${convertToVwWeb(32)} ${convertToVwWeb(20)} ${convertToVwWeb(52)};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(28)};
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(16)};
`;

const ProfileImageWrapper = styled.div<{ status?: string }>`
  width: ${convertToVwWeb(124)};
  height: ${convertToVwWeb(124)};
  border-radius: ${convertToVwWeb(48)};
  position: relative;
  background: ${({ status }) => {
    if (status === 'in-progress') return 'linear-gradient(135deg, #B9F3FF 0%, #FFFFFF 100%)';
    if (status === 'complete') return 'linear-gradient(135deg, #2BC2E1 0%, #FFFFFF 100%)';
    return '#f0f1f2';
  }};
  padding: ${({ status }) => (status === 'in-progress' || status === 'complete' ? '4px' : '0')};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileImage = styled.img<{ $contain?: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: calc(${convertToVwWeb(48)} - 4px);

  object-fit: ${({ $contain }) => ($contain ? 'contain' : 'cover')};
  background-color: white;
  z-index: 1;
`;

const CameraIcon = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${convertToVwWeb(40)};
  height: ${convertToVwWeb(40)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ProfileName = styled.p`
  ${theme.typography.head.semiBold20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${convertToVwWeb(36)} ${convertToVwWeb(32)};
  width: 100%;
  max-width: ${convertToVwWeb(742)};
  align-items: center;
`;

const InputBox = styled.div<{ disabled?: boolean; error?: boolean; fullWidth?: boolean; active?: boolean }>`
  background-color: ${props => (props.disabled ? theme.colors.grayScale[10] : 'white')};
  border: ${props => (props.error ? '1.6px solid #ff0000' : props.active ? `1.6px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`)};
  border-radius: ${convertToVwWeb(4)};
  padding: ${convertToVwWeb(8)} ${convertToVwWeb(12)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(2)};
  position: relative;
  grid-column: ${props => (props.fullWidth ? '1 / span 2' : 'auto')};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(4)};
  width: 100%;
  position: relative;
`;

const UnderlineInputWrapper = styled.div<{ $error?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(8)};
  border-bottom: 1px solid ${({ $error, theme }) => ($error ? '#ff0000' : '#BCBCBC')};
  padding-bottom: ${convertToVwWeb(8)};

  &:focus-within {
    border-bottom: 1px solid ${({ $error, theme }) => ($error ? '#ff0000' : theme.colors.primaryBlue[40])};
  }
`;

const UnderlineInput = styled.input`
  flex: 1;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  ${theme.typography.contents.regular24};
  color: ${theme.colors.grayScale[90]};

  &::placeholder {
    color: ${theme.colors.grayScale[70]};
  }
`;

const UnderlineClearBtn = styled.button`
  width: ${convertToVwWeb(24)};
  height: ${convertToVwWeb(24)};
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
`;

const OrganizationOtherField = styled.div`
  margin-top: ${convertToVwWeb(8)};
  width: 100%;
`;

const InputLabel = styled.label`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
`;

const InputValue = styled.p<{ placeholder?: boolean; error?: boolean; disabled?: boolean }>`
  ${theme.typography.contents.regular24};
  color: ${props => (props.placeholder || props.disabled ? theme.colors.grayScale[50] : props.error ? '#ff0000' : theme.colors.grayScale[90])};
  margin: 0;
`;

const StyledInput = styled.input<{ disabled?: boolean; error?: boolean }>`
  ${theme.typography.contents.regular24};
  color: ${props => (props.disabled ? theme.colors.grayScale[50] : props.error ? '#ff0000' : theme.colors.grayScale[90])};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwWeb(4)};
  margin-top: ${convertToVwWeb(4)};
  z-index: 10;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  max-height: ${convertToVwWeb(200)};
  overflow-y: auto;
`;

const DropdownGroup = styled.div`
  padding: ${convertToVwWeb(6)} 0;
  border-bottom: 1px solid ${theme.colors.grayScale[10]};

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownGroupLabel = styled.div`
  padding: ${convertToVwWeb(8)} ${convertToVwWeb(12)};
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  cursor: default;
`;

const DropdownItem = styled.div<{ active?: boolean }>`
  padding: ${convertToVwWeb(12)};
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  background-color: ${props => (props.active ? theme.colors.grayScale[10] : 'white')};
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.grayScale[10]};
  }
`;

const OrganizationDropdownItem = styled(DropdownItem)`
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(12)};
`;

const OrganizationOptionName = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
`;

const ErrorText = styled.p`
  ${theme.typography.contents.regular20};
  color: #ff0000;
  margin: 0;
  align-self: flex-start;
`;

const IconWrapper = styled.div<{ rotate?: boolean }>`
  position: absolute;
  right: ${convertToVwWeb(12)};
  top: 50%;
  transform: translateY(-50%) ${props => (props.rotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  width: ${convertToVwWeb(20)};
  height: ${convertToVwWeb(20)};
  transition: transform 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${convertToVwWeb(35)};
  align-items: center;
`;

const RadioItem = styled.div`
  display: flex;
  gap: ${convertToVwWeb(10)};
  align-items: center;
  cursor: pointer;
`;

const RadioCircle = styled.div<{ active?: boolean }>`
  width: ${convertToVwWeb(24)};
  height: ${convertToVwWeb(24)};
  border-radius: 50%;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwWeb(12)};
    height: ${convertToVwWeb(12)};
    border-radius: 50%;
    background: ${theme.colors.primaryBlue[40]};
    transform: ${({ active }) =>
      active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const RadioLabel = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: ${convertToVwWeb(12)};
  align-items: center;
  cursor: pointer;
`;

const CheckboxSquare = styled.div<{ active?: boolean; checkColor?: string }>`
  width: ${convertToVwWeb(20)};
  height: ${convertToVwWeb(20)};
  border: 1px solid ${props => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[40])};
  border-radius: ${convertToVwWeb(2)};
  background-color: ${props => (props.active ? theme.colors.primaryBlue[40] : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;

  ${props =>
    props.active &&
    `
    &::after {
      content: '✓';
      color: ${props.checkColor || 'white'};
      font-size: ${convertToVwWeb(14)};
    }
  `}
`;

const UploadBox = styled.div<{ error?: boolean; disabled?: boolean }>`
  width: ${convertToVwWeb(742)};
  height: ${convertToVwWeb(356)};
  border: 1px dashed ${props =>
    props.disabled ? '#EEE' : props.error ? '#ff0000' : '#EEE'};
  border-radius: ${convertToVwWeb(12)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwWeb(16)};
  position: relative;
  overflow: hidden;
  background-color: ${({ disabled }) => (disabled ? '#EEE' : 'white')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const UploadIcon = styled.div`
  width: ${convertToVwWeb(56)};
  height: ${convertToVwWeb(56)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const UploadText = styled.p<{ $disabled?: boolean }>`
  ${theme.typography.contents.regular16};
  color: ${({ $disabled }) => ($disabled ? '#BCBCBC' : theme.colors.grayScale[70])};
  margin: 0;
  text-align: center;
  font-size: ${convertToVwWeb(16)};
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
  margin-top: ${convertToVwWeb(12)};
`;

const UploadButton = styled.div<{ $disabled?: boolean }>`
  width: ${convertToVwWeb(200)};
  height: ${convertToVwWeb(48)};
  padding: ${convertToVwWeb(10)} ${convertToVwWeb(20)};
  background-color: ${({ $disabled }) => ($disabled ? '#BCBCBC' : theme.colors.primaryBlue[80])};
  color: white;
  border-radius: ${convertToVwWeb(5)};
  ${theme.typography.contents.medium20};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwWeb(16)};
  z-index: 2;
`;

const ImagePreviewOverlay = styled(PreviewOverlay)<{ $imageUrl: string }>`
  background: ${({ $imageUrl }) =>
    `linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%), url(${$imageUrl}) lightgray 50% / cover no-repeat`};
`;

const PreviewImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const PreviewDimLayer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1;
  pointer-events: none;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background-color: white;
  padding: ${convertToVwWeb(30)} ${convertToVwWeb(20)} ${convertToVwWeb(20)};
  border-radius: ${convertToVwWeb(20)};
  width: ${convertToVwWeb(320)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(20)};
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.p`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
  text-align: center;
`;

const ModalDesc = styled.div`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[90]};
  text-align: center;
  line-height: 150%;
  p {
    margin: 0;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: ${convertToVwWeb(8)};
  width: 100%;
  justify-content: center;
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  height: ${convertToVwWeb(48)};
  border-radius: ${convertToVwWeb(12)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;

  ${props => props.primary ? `
    padding: ${convertToVwWeb(10)} ${convertToVwWeb(28)};
    background-color: ${theme.colors.primaryBlue[80]};
    color: white;
    border: none;
  ` : `
    padding: ${convertToVwWeb(10)} ${convertToVwWeb(24)};
    background-color: ${theme.colors.grayScale[40]};
    color: ${theme.colors.grayScale[90]};
    border: none;
  `}
`;

const VerificationBadge = styled.div<{ status: 'in-progress' | 'complete' }>`
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(4)};
  padding: ${convertToVwWeb(4)} ${convertToVwWeb(12)};
  border-radius: ${convertToVwWeb(16)};
  background-color: ${props => props.status === 'in-progress' ? '#DEF9FF' : theme.colors.primaryBlue[40]};

  img {
    width: ${convertToVwWeb(20)};
    height: ${convertToVwWeb(20)};
    object-fit: contain;
  }

  p {
    ${theme.typography.chip.semiBold16};
    color: ${props => props.status === 'in-progress' ? theme.colors.primaryBlue[40] : 'white'};
    margin: 0;
  }
`;

const CardWithButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(24)};
  width: 100%;
`;

/** 흰 카드(SectionContainer + ButtonGroup)만 묶어서 노란 TipBox와 시각적으로 분리 */
const FormCardWithFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: 16px 31px 29px;
  border: 1px solid ${theme.colors.grayScale[20]};
  border-top: 1px solid ${theme.colors.grayScale[20]};
  background: ${theme.colors.grayScale[0]};
  border-radius: 0 0 ${convertToVwWeb(12)} ${convertToVwWeb(12)};
`;

const TipBelowCardOuter = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const TipBox = styled.div`
  background-color: ${theme.colors.yellow[5]};
  border: 1px solid ${theme.colors.yellow[10]};
  border-radius: ${convertToVwWeb(20)};
  padding: 28px 48px;
  display: flex;
  gap: ${convertToVwWeb(12)};
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const TipIcon = styled.div`
  width: ${convertToVwWeb(34)};
  height: ${convertToVwWeb(34)};
  min-width: ${convertToVwWeb(34)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const TipText = styled.p`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
  flex: 1;
  line-height: 1.5;
`;

const BankbookUploadedPanel = styled.div`
  width: 100%;
  max-width: ${convertToVwWeb(742)};
  box-sizing: border-box;
  border-radius: 12px;
  background: var(--PrimaryBlue-5, #f8fdff);
  min-height: ${convertToVwWeb(256)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${convertToVwWeb(28)} ${convertToVwWeb(24)};
  gap: ${convertToVwWeb(16)};
`;

const BankbookUploadedMessage = styled.p`
  margin: 0;
  color: var(--PrimaryBlue-80, #026478);
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 500;
  line-height: ${convertToVwWeb(28)};
  text-align: center;
`;

function BankbookUploadedCheckSvg({ size }: { size: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 75 75" fill="none" style={{ width: size, height: size, display: 'block' }} aria-hidden>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M37.5 75C42.4246 75 47.3009 74.03 51.8506 72.1455C56.4003 70.2609 60.5343 67.4987 64.0165 64.0165C67.4987 60.5343 70.2609 56.4003 72.1455 51.8506C74.03 47.3009 75 42.4246 75 37.5C75 32.5754 74.03 27.6991 72.1455 23.1494C70.2609 18.5997 67.4987 14.4657 64.0165 10.9835C60.5343 7.5013 56.4003 4.73907 51.8506 2.85452C47.3009 0.969966 42.4246 -7.33818e-08 37.5 0C27.5544 1.48201e-07 18.0161 3.95088 10.9835 10.9835C3.95088 18.0161 0 27.5544 0 37.5C0 47.4456 3.95088 56.9839 10.9835 64.0165C18.0161 71.0491 27.5544 75 37.5 75ZM36.5333 52.6667L57.3667 27.6667L50.9667 22.3333L33.05 43.8292L23.7792 34.5542L17.8875 40.4458L30.3875 52.9458L33.6125 56.1708L36.5333 52.6667Z"
        fill="#026478"
      />
    </svg>
  );
}

// --- Components ---

const MyAccountWeb = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('translation');
  // 인증된 사용자 ID 가져오기
  const authUser = useAppSelector(selectAuthUser);
  const userId = authUser?.user_id; // || '240F3A70'; // 테스트용 기본값

  // useMyAccount hook에서 모든 상태와 핸들러 가져오기
  const {
    isLoading,
    isSaving,
    userProfile,
    activeTab,
    setActiveTab,
    profileImage,
    setProfileImage,
    userType,
    organization,
    setOrganization,
    organizationOther,
    setOrganizationOther,
    fullName,
    contactName,
    setContactName,
    birthDate,
    setBirthDate,
    isBirthDateLocked,
    isBankInfoLocked,
    isVerificationLocked,
    phone,
    email,
    country,
    setCountry,
    language,
    setLanguage,
    certificateFile,
    setCertificateFile,
    ministryCountry,
    setMinistryCountry,
    isSecurityCountry,
    setIsSecurityCountry,
    bankCountry,
    setBankCountry,
    bankName,
    setBankName,
    accountNumber,
    setAccountNumber,
    accountHolder,
    setAccountHolder,
    bankbookFile,
    setBankbookFile,
    streetAddress,
    setStreetAddress,
    city,
    setCity,
    state,
    setState,
    zipCode,
    setZipCode,
    hasNoUniqueId,
    setHasNoUniqueId,
    uniqueId,
    setUniqueId,
    residentRegistrationNumber,
    setResidentRegistrationNumber,
    idFile,
    setIdFile,
    verificationStatus,
    setVerificationStatus,
    errors,
    setErrors,
    showErrors,
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    handleSave,
    handleCancel,
    handleNext,
    handlePrev,
    handleClearAll,
    isDirty,
  } = useMyAccount(userId);

  const isStepMode = verificationStatus === 'none';

  // 드롭다운 상태 (로컬에서만 관리)
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = React.useState(false);
  const [isOrganizationDropdownOpen, setIsOrganizationDropdownOpen] = React.useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = React.useState(false);
  const [isBankDropdownOpen, setIsBankDropdownOpen] = React.useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = React.useState(false);
  const [isMinistryCountryDropdownOpen, setIsMinistryCountryDropdownOpen] = React.useState(false);
  const [isBirthDatePickerOpen, setIsBirthDatePickerOpen] = React.useState(false);
  useEffect(() => {
    if (isBirthDateLocked) setIsBirthDatePickerOpen(false);
  }, [isBirthDateLocked]);
  useEffect(() => {
    if (isBankInfoLocked) {
      setIsBankDropdownOpen(false);
      setIsStateDropdownOpen(false);
    }
  }, [isBankInfoLocked]);
  const [profileImageError, setProfileImageError] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const organizationDropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const ministryCountryDropdownRef = useRef<HTMLDivElement>(null);
  const bankDropdownRef = useRef<HTMLDivElement>(null);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const pendingCancelHandlerRef = useRef<(() => void) | null>(null);

  const handleRequestCancel = () => {
    setIsCancelConfirmOpen(true);
  };

  const handleConfirmCancel = () => {
    pendingCancelHandlerRef.current?.();
    pendingCancelHandlerRef.current = null;
    setIsCancelConfirmOpen(false);
  };

  useEffect(() => {
    setProfileImageError(false);
  }, [profileImage]);

  // 모든 드롭다운: 바깥 영역 클릭 시 닫기
  const closeAllDropdowns = useCallback(() => {
    setIsCountryDropdownOpen(false);
    setIsOrganizationDropdownOpen(false);
    setIsLanguageDropdownOpen(false);
    setIsMinistryCountryDropdownOpen(false);
    setIsBankDropdownOpen(false);
    setIsStateDropdownOpen(false);
  }, []);
  useClickOutside(
    closeAllDropdowns,
    countryDropdownRef,
    organizationDropdownRef,
    languageDropdownRef,
    ministryCountryDropdownRef,
    bankDropdownRef,
    stateDropdownRef,
  );

  const countryOptions = getCountryOptions();
  const organizationOptions = React.useMemo(
    () => getOrganizationOptions(),
    [i18n.language, i18n.resolvedLanguage],
  );
  const organizationDisplayName = getOrganizationDisplayName(organization);
  const profileOrgSuffix = organization === ORGANIZATION_ETC_CODE && organizationOther.trim()
    ? organizationOther.trim()
    : organizationDisplayName;
  const languages = [
    t('common.languages.ko'),
    t('common.languages.en'),
  ];
  const bankGroups = React.useMemo(
    () =>
      (t('page.myAccount.bankGroupsData', {
        returnObjects: true,
        defaultValue: [],
      }) as unknown as Array<{ type: string; label: string; options: string[] }>),
    [t],
  );
  const states = US_STATES;
  const ministryCountries = countryOptions;

  // 로딩 상태 표시
  if (isLoading) {
    return (
      <WebPageLayout title={t('page.myAccount.pageTitle')}>
        <PageContainer>
          <ContentWrapper>
            <DSLoading />
          </ContentWrapper>
        </PageContainer>
      </WebPageLayout>
    );
  }

  const renderStepIndicator = () => {
    const steps = [1, 2, 3, 4];
    const currentStep = activeTab === 'basic' ? 1 : activeTab === 'ministry' ? 2 : activeTab === 'bank' ? 3 : 4;

    const stepListEl = (
      <StepList>
        <StepLine />
        {steps.map(step => (
          <StepCircle key={step} active={step === currentStep}>
            <p>{step}</p>
          </StepCircle>
        ))}
      </StepList>
    );

    return (
      <StepIndicatorWrapper>
        <StepHintStack>
          <StepHintBlackBlock>
            <StepText>{t('page.myAccount.stepHint')}</StepText>
            <StepText>{t('page.myAccount.stepHintLine2')}</StepText>
          </StepHintBlackBlock>
          <StepSubText>{t(`page.myAccount.stepSubHint.${activeTab}`)}</StepSubText>
          <StepProgressRow>{stepListEl}</StepProgressRow>
        </StepHintStack>
      </StepIndicatorWrapper>
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (value: string | null) => void, errorKey?: string) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(reader.result as string);
        if (errorKey && errors[errorKey]) {
          setErrors({ ...errors, [errorKey]: false });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const renderBasicInfo = () => {
    const isIndividual = isIndividualUserType(userProfile?.user_type);
    
    const nameLabel = isIndividual ? t('page.myAccount.labels.name') : t('page.myAccount.labels.contactName');
    const birthLabel = isIndividual ? t('page.myAccount.labels.birthDate') : t('page.myAccount.labels.contactBirthDate');

    return (
    <>
      <ProfileSection>
        <ProfileImageWrapper status={verificationStatus}>
          <input
            type="file"
            id="profile-upload"
            style={{ display: 'none' }}
            accept="image/*"
            onChange={(e) => handleFileChange(e, setProfileImage)}
          />
          {profileImage?.trim() && !profileImageError ? (
            <ProfileImage src={profileImage} alt="" onError={() => setProfileImageError(true)} />
          ) : (
            <ProfileImage src={defaultProfileIcon} alt={t('page.myAccount.profile.defaultAlt')} $contain />
          )}
          <CameraIcon onClick={() => document.getElementById('profile-upload')?.click()}>
            <img src={photoIcon} alt="camera" />
          </CameraIcon>
        </ProfileImageWrapper>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: convertToVwWeb(4) }}>
          <ProfileName>
            {`${isIndividual ? fullName : contactName}${profileOrgSuffix ? ` (${profileOrgSuffix})` : ''}`}
          </ProfileName>
          {verificationStatus === 'in-progress' && (
            <VerificationBadge status="in-progress">
              <img src={profileCertificateIcon} alt="certificate" />
              <p>{t('page.myAccount.verification.inProgress')}</p>
            </VerificationBadge>
          )}
          {verificationStatus === 'complete' && (
            <VerificationBadge status="complete">
              <img src={profileCertificateFilledIcon} alt="certificate-filled" />
              <p>{t('page.myAccount.verification.complete')}</p>
            </VerificationBadge>
          )}
        </div>
      </ProfileSection>
      <InputGrid>
        <InputBox disabled style={isIndividual && isOrganizationDropdownForIndividual(userProfile?.user_type) && organization === ORGANIZATION_ETC_CODE ? { alignSelf: 'start' } : undefined}>
          <InputLabel>{t('page.myAccount.labels.userType')}</InputLabel>
          <InputValue disabled>{userType || t('page.myAccount.placeholders.userType')}</InputValue>
        </InputBox>
        
        {/* Organization: 선교사/선교단체 사역자 → 드롭다운(소속 기관명), 교회 사역자/개인 기타/단체 → 텍스트(소속 단체명) */}
        <InputGroup ref={organizationDropdownRef} style={isIndividual && isOrganizationDropdownForIndividual(userProfile?.user_type) && organization === ORGANIZATION_ETC_CODE ? { alignSelf: 'start' } : undefined}>
          {isIndividual && isOrganizationDropdownForIndividual(userProfile?.user_type) ? (
            <>
              <InputBox active={isOrganizationDropdownOpen} error={showErrors && errors.organization} onClick={() => { const wasOpen = isOrganizationDropdownOpen; closeAllDropdowns(); if (!wasOpen) setIsOrganizationDropdownOpen(true); }}>
                {organization && <InputLabel>{t('page.myAccount.labels.organizationInstitution')}</InputLabel>}
                <InputValue placeholder={!organization}>{getOrganizationDisplayName(organization) || t('page.myAccount.labels.organizationInstitution')}</InputValue>
                <IconWrapper rotate={isOrganizationDropdownOpen}>
                  <img src={dropdownIcon} alt="dropdown" />
                </IconWrapper>
                {isOrganizationDropdownOpen && (
                  <DropdownList onClick={(e) => e.stopPropagation()}>
                    {organizationOptions.map((opt) => (
                      <DropdownItem key={`${opt.code}-${opt.name}`} onClick={() => {
                        setOrganization(opt.code);
                        if (opt.code !== ORGANIZATION_ETC_CODE) setOrganizationOther('');
                        setIsOrganizationDropdownOpen(false);
                        if (errors.organization) setErrors({ ...errors, organization: false, organizationOther: false });
                      }}>
                        {opt.name}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </InputBox>
              {showErrors && errors.organization && <ErrorText>{t('common.errors.required')}</ErrorText>}
              {organization === ORGANIZATION_ETC_CODE && (
                <OrganizationOtherField>
                  <UnderlineInputWrapper $error={showErrors && errors.organizationOther}>
                    <UnderlineInput
                      placeholder={t('page.myAccount.labels.organizationOther')}
                      value={organizationOther}
                      onChange={(e) => {
                        setOrganizationOther(e.target.value);
                        if (errors.organizationOther) setErrors({ ...errors, organizationOther: false });
                      }}
                    />
                    {organizationOther && (
                      <UnderlineClearBtn type="button" onClick={() => setOrganizationOther('')}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" fill="#BCBCBC" />
                          <path d="M8 8L16 16M16 8L8 16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </UnderlineClearBtn>
                    )}
                  </UnderlineInputWrapper>
                  {showErrors && errors.organizationOther && <ErrorText>{t('common.errors.required')}</ErrorText>}
                </OrganizationOtherField>
              )}
            </>
          ) : (
            <>
              <InputBox error={showErrors && errors.organization}>
                {organization && <InputLabel>{t('page.myAccount.labels.organization')}</InputLabel>}
                <StyledInput
                  placeholder={t('page.myAccount.placeholders.organization')}
                  value={organization}
                  onChange={(e) => {
                    setOrganization(e.target.value);
                    if (errors.organization) setErrors({ ...errors, organization: false });
                  }}
                />
                {organization && (
                  <IconWrapper onClick={() => setOrganization('')}>
                    <img src={deleteIcon} alt="clear" />
                  </IconWrapper>
                )}
              </InputBox>
              {showErrors && errors.organization && <ErrorText>{t('common.errors.required')}</ErrorText>}
            </>
          )}
        </InputGroup>
        {isIndividual ? (
          <InputBox disabled>
            <InputLabel>{nameLabel}</InputLabel>
            <InputValue disabled>{fullName || t('page.myAccount.placeholders.name')}</InputValue>
          </InputBox>
        ) : (
          <InputGroup>
            <InputBox error={showErrors && errors.contactName}>
              {contactName && <InputLabel>{nameLabel}</InputLabel>}
              <StyledInput
                placeholder={nameLabel}
                value={contactName}
                onChange={(e) => {
                  setContactName(e.target.value);
                  if (errors.contactName) setErrors({ ...errors, contactName: false });
                }}
              />
              {contactName && (
                <IconWrapper onClick={() => setContactName('')}>
                  <img src={deleteIcon} alt="clear" />
                </IconWrapper>
              )}
            </InputBox>
            {showErrors && errors.contactName && <ErrorText>{t('common.errors.required')}</ErrorText>}
          </InputGroup>
        )}
        <InputGroup>
          <InputBox
            disabled={isBirthDateLocked}
            error={!isBirthDateLocked && showErrors && errors.birthDate}
            onClick={isBirthDateLocked ? undefined : () => setIsBirthDatePickerOpen(true)}
            style={{ cursor: isBirthDateLocked ? 'not-allowed' : 'pointer' }}
          >
            <InputLabel>{birthLabel}</InputLabel>
            <InputValue placeholder={!birthDate} disabled={isBirthDateLocked}>
              {birthDate || birthLabel}
            </InputValue>
            {!isBirthDateLocked ? (
              <IconWrapper>
                <img src={calendarIcon} alt="calendar" />
              </IconWrapper>
            ) : null}
          </InputBox>
          {!isBirthDateLocked && showErrors && errors.birthDate ? (
            <ErrorText>{t('common.errors.required')}</ErrorText>
          ) : null}
        </InputGroup>
        {!isBirthDateLocked ? (
          <BirthDatePicker
            isOpen={isBirthDatePickerOpen}
            value={birthDate}
            onConfirm={(v) => {
              setBirthDate(v);
              if (errors.birthDate) setErrors({ ...errors, birthDate: false });
              setIsBirthDatePickerOpen(false);
            }}
            onCancel={() => setIsBirthDatePickerOpen(false)}
          />
        ) : null}
        <InputBox disabled>
          <InputLabel>{t('page.myAccount.labels.phone')}</InputLabel>
          <InputValue disabled>{phone || t('page.myAccount.placeholders.phone')}</InputValue>
        </InputBox>
        <InputBox disabled>
          <InputLabel>{t('page.myAccount.labels.email')}</InputLabel>
          <InputValue disabled>{email || t('page.myAccount.placeholders.email')}</InputValue>
        </InputBox>
        <InputGroup ref={countryDropdownRef}>
          <InputBox active={isCountryDropdownOpen} error={showErrors && errors.country} onClick={() => { const wasOpen = isCountryDropdownOpen; closeAllDropdowns(); if (!wasOpen) setIsCountryDropdownOpen(true); }}>
            {country && <InputLabel>{t('page.myAccount.labels.country')}</InputLabel>}
            <InputValue placeholder={!country}>{getCountryLabel(country) || t('page.myAccount.labels.country')}</InputValue>
            <IconWrapper rotate={isCountryDropdownOpen}>
              <img src={dropdownIcon} alt="dropdown" />
            </IconWrapper>
            {isCountryDropdownOpen && (
              <DropdownList onClick={(e) => e.stopPropagation()}>
                {countryOptions.map(c => (
                  <DropdownItem key={c.value} onClick={() => {
                    setCountry(c.value);
                    setIsCountryDropdownOpen(false);
                    if (errors.country) setErrors({ ...errors, country: false });
                  }}>
                    {c.label}
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </InputBox>
          {showErrors && errors.country && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </InputGroup>
        <InputGroup ref={languageDropdownRef}>
          <InputBox active={isLanguageDropdownOpen} error={showErrors && errors.language} onClick={() => { const wasOpen = isLanguageDropdownOpen; closeAllDropdowns(); if (!wasOpen) setIsLanguageDropdownOpen(true); }}>
            {language && <InputLabel>{t('page.myAccount.labels.language')}</InputLabel>}
            <InputValue placeholder={!language}>{language || t('page.myAccount.labels.language')}</InputValue>
            <IconWrapper rotate={isLanguageDropdownOpen}>
              <img src={dropdownIcon} alt="dropdown" />
            </IconWrapper>
            {isLanguageDropdownOpen && (
              <DropdownList onClick={(e) => e.stopPropagation()}>
                {languages.map(lang => (
                  <DropdownItem key={lang} onClick={() => {
                    setLanguage(lang);
                    setIsLanguageDropdownOpen(false);
                    if (errors.language) setErrors({ ...errors, language: false });
                  }}>
                    {lang}
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </InputBox>
          {showErrors && errors.language && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </InputGroup>
      </InputGrid>
    </>
    );
  };

  const renderMinistryInfo = () => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(8) }}>
        <InputLabel style={{ color: theme.colors.grayScale[70] }}>{t('page.myAccount.labels.certificate')}</InputLabel>
        <input
          type="file"
          id="certificate-upload"
          style={{ display: 'none' }}
          accept="image/*,application/pdf"
          onChange={(e) => handleFileChange(e, setCertificateFile, 'hasCertificate')}
        />
        <UploadBox
          error={showErrors && errors.hasCertificate}
          onClick={() => document.getElementById('certificate-upload')?.click()}
          style={{ cursor: 'pointer' }}
        >
          {certificateFile ? (
            <PreviewOverlay>
              <PreviewImage src={certificateFile} />
              <PreviewDimLayer />
              <div style={{ zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: convertToVwWeb(16) }}>
                <UploadIcon>
                  <img src={certificateIcon} alt="upload" style={{ width: '100%', filter: 'brightness(0) invert(1)' }} />
                </UploadIcon>
                <UploadText style={{ color: theme.colors.grayScale[0] }}>{t('page.myAccount.upload.certificate')}</UploadText>
                <UploadButton onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('certificate-upload')?.click();
                }}>{t('common.actions.selectFile')}</UploadButton>
              </div>
            </PreviewOverlay>
          ) : (
            <>
              <UploadIcon>
                <img src={certificateIcon} alt="upload" style={{ width: '100%' }} />
              </UploadIcon>
              <UploadText>{t('page.myAccount.upload.certificate')}</UploadText>
              <UploadButton>{t('common.actions.selectFile')}</UploadButton>
            </>
          )}
        </UploadBox>
        {showErrors && errors.hasCertificate && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
      </div>
      <InputGrid>
        <InputGroup ref={ministryCountryDropdownRef}>
          <InputBox active={isMinistryCountryDropdownOpen} error={showErrors && errors.ministryCountry} onClick={() => { const wasOpen = isMinistryCountryDropdownOpen; closeAllDropdowns(); if (!wasOpen) setIsMinistryCountryDropdownOpen(true); }}>
            {ministryCountry && <InputLabel style={{ fontSize: '20px' }}>{t('page.myAccount.labels.ministryCountry')}</InputLabel>}
            <InputValue placeholder={!ministryCountry}>{getCountryLabel(ministryCountry) || t('page.myAccount.labels.ministryCountry')}</InputValue>
            <IconWrapper rotate={isMinistryCountryDropdownOpen}>
              <img src={dropdownIcon} alt="dropdown" />
            </IconWrapper>
            {isMinistryCountryDropdownOpen && (
              <DropdownList onClick={(e) => e.stopPropagation()}>
                {countryOptions.map(c => (
                  <DropdownItem key={c.value} onClick={() => {
                    setMinistryCountry(c.value);
                    setIsMinistryCountryDropdownOpen(false);
                    if (errors.ministryCountry) setErrors({ ...errors, ministryCountry: false });
                  }}>
                    {c.label}
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </InputBox>
          {showErrors && errors.ministryCountry && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </InputGroup>
        <InputBox>
          <InputLabel style={{ fontSize: '20px' }}>{t('page.myAccount.labels.securityNote')}</InputLabel>
          <CheckboxGroup onClick={() => setIsSecurityCountry(!isSecurityCountry)}>
            <CheckboxSquare active={isSecurityCountry} />
            <RadioLabel style={{ fontSize: '24px' }}>{t('page.myAccount.labels.securityCountry')}</RadioLabel>
          </CheckboxGroup>
        </InputBox>
      </InputGrid>
    </>
  );

  const renderBankInfo = () => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(12) }}>
        <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.countryTitle')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(8) }}>
          {isBankInfoLocked ? (
            <>
              <p style={{ ...theme.typography.contents.medium20, margin: 0, color: theme.colors.grayScale[90] }}>
                {bankCountry === 'KR' ? t('page.myAccount.bank.krLockedIntro') : t('page.myAccount.bank.usLockedIntro')}
              </p>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>
                {bankCountry === 'KR' ? t('page.myAccount.bank.krHint') : t('page.myAccount.bank.usHint')}
              </p>
            </>
          ) : (
            <>
              <RadioGroup>
                <RadioItem onClick={() => setBankCountry('KR')}>
                  <RadioCircle active={bankCountry === 'KR'} />
                  <RadioLabel>{t('common.countries.KR')}</RadioLabel>
                </RadioItem>
                <RadioItem onClick={() => setBankCountry('US')}>
                  <RadioCircle active={bankCountry === 'US'} />
                  <RadioLabel>{t('common.countries.US')}</RadioLabel>
                </RadioItem>
              </RadioGroup>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>
                {bankCountry === 'KR' ? t('page.myAccount.bank.krHint') : t('page.myAccount.bank.usHint')}
              </p>
            </>
          )}
        </div>
      </div>

      {bankCountry === 'KR' && isBankInfoLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.infoTitle')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(16) }}>
            <InputGrid style={{ maxWidth: 'none' }}>
              <InputBox disabled>
                <InputLabel>{t('page.myAccount.labels.bankName')}</InputLabel>
                <InputValue disabled>{bankName || t('page.myAccount.labels.bankName')}</InputValue>
              </InputBox>
              <InputBox disabled>
                <InputLabel>{t('page.myAccount.labels.accountNumber')}</InputLabel>
                <InputValue disabled>{accountNumber || t('page.myAccount.labels.accountNumber')}</InputValue>
              </InputBox>
            </InputGrid>
            <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(8) }}>
              <InputBox disabled style={{ width: convertToVwWeb(353) }}>
                <InputLabel>{t('page.myAccount.labels.accountHolder')}</InputLabel>
                <InputValue disabled>{accountHolder || t('page.myAccount.labels.accountHolder')}</InputValue>
              </InputBox>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>{t('page.myAccount.bank.nameHelp')}</p>
            </div>
          </div>
        </div>
      ) : bankCountry === 'KR' ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.infoTitle')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(16) }}>
            <InputGrid style={{ maxWidth: 'none' }}>
              <InputGroup ref={bankDropdownRef}>
                <InputBox active={isBankDropdownOpen} error={showErrors && errors.bankName} onClick={() => { const wasOpen = isBankDropdownOpen; closeAllDropdowns(); if (!wasOpen) setIsBankDropdownOpen(true); }}>
                  {bankName && <InputLabel>{t('page.myAccount.labels.bankName')}</InputLabel>}
                  <InputValue placeholder={!bankName}>{bankName || t('page.myAccount.labels.bankName')}</InputValue>
                  <IconWrapper rotate={isBankDropdownOpen}>
                    <img src={dropdownIcon} alt="dropdown" />
                  </IconWrapper>
                  {isBankDropdownOpen && (
                    <DropdownList onClick={(e) => e.stopPropagation()}>
                      {bankGroups.map((group) => (
                        <DropdownGroup key={group.label}>
                          <DropdownGroupLabel>{group.label}</DropdownGroupLabel>
                          {group.options.map((bank) => (
                            <DropdownItem key={bank} onClick={() => {
                              setBankName(bank);
                              setIsBankDropdownOpen(false);
                              if (errors.bankName) setErrors({ ...errors, bankName: false });
                            }}>
                              {bank}
                            </DropdownItem>
                          ))}
                        </DropdownGroup>
                      ))}
                    </DropdownList>
                  )}
                </InputBox>
                {showErrors && errors.bankName && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox error={showErrors && errors.accountNumber}>
                  {accountNumber && <InputLabel>{t('page.myAccount.labels.accountNumber')}</InputLabel>}
                  <StyledInput
                    placeholder={t('page.myAccount.labels.accountNumber')}
                    value={accountNumber}
                    onChange={(e) => {
                      setAccountNumber(e.target.value.replace(/[^0-9]/g, ''));
                      if (errors.accountNumber) setErrors({ ...errors, accountNumber: false });
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    type="tel"
                  />
                  {accountNumber && (
                    <IconWrapper onClick={() => setAccountNumber('')}>
                      <img src={deleteIcon} alt="clear" />
                    </IconWrapper>
                  )}
                </InputBox>
                {showErrors && errors.accountNumber && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
            </InputGrid>
            <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(8) }}>
              <InputGroup>
                <InputBox style={{ width: convertToVwWeb(353) }} error={showErrors && errors.accountHolder}>
                  {accountHolder && <InputLabel>{t('page.myAccount.labels.accountHolder')}</InputLabel>}
                  <StyledInput
                    placeholder={t('page.myAccount.labels.accountHolder')}
                    value={accountHolder}
                    onChange={(e) => {
                      setAccountHolder(e.target.value);
                      if (errors.accountHolder) setErrors({ ...errors, accountHolder: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.accountHolder && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>{t('page.myAccount.bank.nameHelp')}</p>
            </div>
          </div>
        </div>
      ) : isBankInfoLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.addressTitle')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(16) }}>
            <InputGrid style={{ maxWidth: 'none' }}>
              <InputGroup>
                <InputBox disabled>
                  <InputLabel>Street Address</InputLabel>
                  <InputValue disabled>{streetAddress}</InputValue>
                </InputBox>
              </InputGroup>
              <InputGroup>
                <InputBox disabled>
                  <InputLabel>City</InputLabel>
                  <InputValue disabled>{city}</InputValue>
                </InputBox>
              </InputGroup>
              <InputGroup>
                <InputBox disabled>
                  <InputLabel>State</InputLabel>
                  <InputValue disabled>{state ? formatUsStateLabel(state) : ''}</InputValue>
                </InputBox>
              </InputGroup>
              <InputGroup>
                <InputBox disabled>
                  <InputLabel>ZIP Code</InputLabel>
                  <InputValue disabled>{zipCode}</InputValue>
                </InputBox>
              </InputGroup>
            </InputGrid>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.addressTitle')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(16) }}>
            <InputGrid style={{ maxWidth: 'none' }}>
              <InputGroup>
                <InputBox error={showErrors && errors.streetAddress}>
                  <InputLabel>Street Address</InputLabel>
                  <StyledInput
                    placeholder="Street Address"
                    value={streetAddress}
                    onChange={(e) => {
                      setStreetAddress(e.target.value);
                      if (errors.streetAddress) setErrors({ ...errors, streetAddress: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.streetAddress && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox error={showErrors && errors.city}>
                  <InputLabel>City</InputLabel>
                  <StyledInput
                    placeholder="City"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      if (errors.city) setErrors({ ...errors, city: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.city && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup ref={stateDropdownRef}>
                <InputBox active={isStateDropdownOpen} error={showErrors && errors.state} onClick={() => { const wasOpen = isStateDropdownOpen; closeAllDropdowns(); if (!wasOpen) setIsStateDropdownOpen(true); }}>
                  <InputLabel>State</InputLabel>
                  <InputValue placeholder={!state}>{state ? formatUsStateLabel(state) : 'State'}</InputValue>
                  <IconWrapper rotate={isStateDropdownOpen}>
                    <img src={dropdownIcon} alt="dropdown" />
                  </IconWrapper>
                  {isStateDropdownOpen && (
                    <DropdownList onClick={(e) => e.stopPropagation()}>
                      {states.map(s => (
                        <DropdownItem key={s.code} onClick={() => {
                          setState(s.code);
                          setIsStateDropdownOpen(false);
                          if (errors.state) setErrors({ ...errors, state: false });
                        }}>
                          {`${s.name} (${s.code})`}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                </InputBox>
                {showErrors && errors.state && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox error={showErrors && errors.zipCode}>
                  <InputLabel>ZIP Code</InputLabel>
                  <StyledInput
                    placeholder="ZIP Code"
                    value={zipCode}
                  onChange={(e) => {
                    setZipCode(e.target.value.replace(/[^0-9]/g, ''));
                    if (errors.zipCode) setErrors({ ...errors, zipCode: false });
                  }}
                  />
                </InputBox>
                {showErrors && errors.zipCode && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
            </InputGrid>
          </div>
        </div>
      )}

      {bankCountry === 'KR' && isBankInfoLocked && (bankbookFile || userProfile?.bankbook_image_url) ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(8) }}>
          <BankbookUploadedPanel>
            <BankbookUploadedCheckSvg size={convertToVwWeb(75)} />
            <BankbookUploadedMessage>{t('page.myAccount.bank.bankbookUploaded')}</BankbookUploadedMessage>
          </BankbookUploadedPanel>
        </div>
      ) : null}
      {bankCountry === 'KR' && !isBankInfoLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(8) }}>
          <input
            type="file"
            id="bankbook-upload"
            style={{ display: 'none' }}
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, setBankbookFile, 'hasBankbookCopy')}
          />
          <UploadBox
            style={{ height: convertToVwWeb(256), cursor: 'pointer' }}
            error={showErrors && errors.hasBankbookCopy}
            onClick={() => document.getElementById('bankbook-upload')?.click()}
          >
            {bankbookFile ? (
              <PreviewOverlay>
                <PreviewImage src={bankbookFile} />
                <PreviewDimLayer />
                <div style={{ zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: convertToVwWeb(16) }}>
                  <UploadIcon style={{ height: convertToVwWeb(48), width: convertToVwWeb(50) }}>
                    <img src={lawIcon} alt="upload" style={{ width: '100%', filter: 'brightness(0) invert(1)' }} />
                  </UploadIcon>
                  <UploadText style={{ color: 'white' }}>{t('page.myAccount.upload.bankbook')}</UploadText>
                  <UploadButton onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById('bankbook-upload')?.click();
                  }}>{t('common.actions.selectFile')}</UploadButton>
                </div>
              </PreviewOverlay>
            ) : (
              <>
                <UploadIcon style={{ height: convertToVwWeb(48), width: convertToVwWeb(50) }}>
                  <img src={lawIcon} alt="upload" style={{ width: '100%' }} />
                </UploadIcon>
                <UploadText>{t('page.myAccount.upload.bankbook')}</UploadText>
                <UploadButton>{t('common.actions.selectFile')}</UploadButton>
              </>
            )}
          </UploadBox>
          {showErrors && errors.hasBankbookCopy && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
        </div>
      ) : null}
    </>
  );

  const renderVerification = () => {
    const isIndividual = isIndividualUserType(userProfile?.user_type);

    return (
    <>
      {isIndividual ? (
        isVerificationLocked ? (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742) }}>
            <InputGroup style={{ maxWidth: convertToVwWeb(353) }}>
              <InputBox disabled>
                <InputLabel style={{ fontSize: '20px' }}>{t('page.myAccount.labels.residentRegistration')}</InputLabel>
                <InputValue disabled>{residentRegistrationNumber || t('page.myAccount.labels.residentRegistration')}</InputValue>
              </InputBox>
            </InputGroup>
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742) }}>
          <InputGroup style={{ maxWidth: convertToVwWeb(353) }}>
            <InputBox error={showErrors && (!!errors.residentRegistration || !!errors.residentRegistrationInvalid)}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {residentRegistrationNumber && (
                  <InputLabel style={{ fontSize: '20px' }}>
                    {t('page.myAccount.labels.residentRegistration')}
                  </InputLabel>
                )}
                <StyledInput
                  placeholder={t('page.myAccount.labels.residentRegistration')}
                  value={residentRegistrationNumber}
                  maxLength={13}
                  onChange={(e) => {
                    setResidentRegistrationNumber(e.target.value);
                    if (errors.residentRegistration || errors.residentRegistrationInvalid) {
                      setErrors({ ...errors, residentRegistration: false, residentRegistrationInvalid: false });
                    }
                  }}
                  error={showErrors && (!!errors.residentRegistration || !!errors.residentRegistrationInvalid)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  type="tel"
                  autoComplete="off"
                />
              </div>
              {residentRegistrationNumber && (
                <IconWrapper onClick={() => setResidentRegistrationNumber('')}>
                  <img src={deleteIcon} alt="clear" />
                </IconWrapper>
              )}
            </InputBox>
            {showErrors && errors.residentRegistration && (
              <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>
            )}
            {showErrors && errors.residentRegistrationInvalid && !errors.residentRegistration && (
              <ErrorText style={{ position: 'static' }}>{t('common.errors.invalidResidentRegistration')}</ErrorText>
            )}
          </InputGroup>
        </div>
        )
      ) : isVerificationLocked ? (
        <div style={{ display: 'flex', gap: convertToVwWeb(36), alignItems: 'flex-end', width: '100%', maxWidth: convertToVwWeb(742) }}>
          <InputBox style={{ width: convertToVwWeb(353), flexShrink: 0 }} disabled>
            <InputLabel>{t('page.myAccount.labels.uniqueId')}</InputLabel>
            <CheckboxGroup style={{ cursor: 'not-allowed' }} onClick={(e) => e.preventDefault()}>
              <CheckboxSquare active={!hasNoUniqueId} />
              <RadioLabel style={{ fontSize: convertToVwWeb(24) }}>{t('page.myAccount.labels.uniqueIdNone')}</RadioLabel>
            </CheckboxGroup>
          </InputBox>
          <InputGroup style={{ width: convertToVwWeb(353), flexShrink: 0 }}>
            <InputBox disabled>
              <InputLabel>{t('page.myAccount.placeholders.uniqueId')}</InputLabel>
              <InputValue disabled>{uniqueId || t('page.myAccount.placeholders.uniqueId')}</InputValue>
            </InputBox>
          </InputGroup>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: convertToVwWeb(36), alignItems: 'flex-end', width: '100%', maxWidth: convertToVwWeb(742) }}>
          <InputBox style={{ width: convertToVwWeb(353), flexShrink: 0 }}>
            <InputLabel>{t('page.myAccount.labels.uniqueId')}</InputLabel>
            <CheckboxGroup onClick={() => {
              setHasNoUniqueId(!hasNoUniqueId);
              if (!hasNoUniqueId) {
                setErrors({ ...errors, uniqueId: false, hasIdCopy: false });
              }
            }}>
              <CheckboxSquare active={!hasNoUniqueId} />
              <RadioLabel style={{ fontSize: convertToVwWeb(24) }}>{t('page.myAccount.labels.uniqueIdNone')}</RadioLabel>
            </CheckboxGroup>
          </InputBox>
          <InputGroup style={{ width: convertToVwWeb(353), flexShrink: 0 }}>
            <InputBox error={showErrors && errors.uniqueId} disabled={hasNoUniqueId}>
              <StyledInput
                placeholder={t('page.myAccount.placeholders.uniqueId')}
                value={uniqueId}
                onChange={(e) => {
                  setUniqueId(e.target.value.replace(/[^0-9]/g, ''));
                  if (errors.uniqueId) setErrors({ ...errors, uniqueId: false });
                }}
                disabled={hasNoUniqueId}
                error={showErrors && errors.uniqueId}
              />
              {uniqueId && !hasNoUniqueId && (
                <IconWrapper onClick={() => setUniqueId('')}>
                  <img src={deleteIcon} alt="clear" />
                </IconWrapper>
              )}
            </InputBox>
            {showErrors && errors.uniqueId && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
          </InputGroup>
        </div>
      )}
      {isVerificationLocked && (idFile || userProfile?.registration_card_url) ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(8) }}>
          <BankbookUploadedPanel>
            <BankbookUploadedCheckSvg size={convertToVwWeb(75)} />
            <BankbookUploadedMessage>{t('page.myAccount.verification.idCopyUploaded')}</BankbookUploadedMessage>
          </BankbookUploadedPanel>
        </div>
      ) : !isVerificationLocked ? (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: convertToVwWeb(742), gap: convertToVwWeb(8) }}>
        <input
          type="file"
          id="id-upload"
          style={{ display: 'none' }}
          accept="image/*,application/pdf"
          onChange={(e) => handleFileChange(e, setIdFile, 'hasIdCopy')}
          disabled={!isIndividual && hasNoUniqueId}
        />
        <UploadBox
          error={showErrors && errors.hasIdCopy}
          disabled={!isIndividual && hasNoUniqueId}
          onClick={() => {
            if (!isIndividual && hasNoUniqueId) return;
            document.getElementById('id-upload')?.click();
          }}
        >
          {(!isIndividual && hasNoUniqueId) ? (
            <>
              <UploadIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none" style={{ width: '100%', height: '100%' }}>
                  <path d="M42.0003 23.3359C43.7966 23.3358 45.5548 23.8541 47.0638 24.8285C48.5728 25.8029 49.7686 27.1921 50.5076 28.8293C51.2467 30.4665 51.4976 32.2822 51.2302 34.0585C50.9628 35.8348 50.1886 37.4962 49.0003 38.8433V48.6246C49.0004 49.0622 48.8886 49.4925 48.6756 49.8747C48.4625 50.2568 48.1553 50.5782 47.7831 50.8083C47.4109 51.0383 46.9861 51.1694 46.549 51.1891C46.1119 51.2087 45.677 51.1163 45.2857 50.9206L42.0003 49.2779L38.715 50.9206C38.3236 51.1163 37.8888 51.2087 37.4517 51.1891C37.0145 51.1694 36.5897 51.0383 36.2175 50.8083C35.8453 50.5782 35.5381 50.2568 35.3251 49.8747C35.1121 49.4925 35.0003 49.0622 35.0003 48.6246V38.8433C33.8121 37.4962 33.0378 35.8348 32.7704 34.0585C32.5031 32.2822 32.754 30.4665 33.493 28.8293C34.232 27.1921 35.4278 25.8029 36.9369 24.8285C38.4459 23.8541 40.204 23.3358 42.0003 23.3359ZM46.667 9.33594C47.9047 9.33594 49.0917 9.8276 49.9668 10.7028C50.842 11.5779 51.3337 12.7649 51.3337 14.0026V18.6693C51.3337 19.2881 51.0878 19.8816 50.6502 20.3192C50.2127 20.7568 49.6192 21.0026 49.0003 21.0026C48.3815 21.0026 47.788 20.7568 47.3504 20.3192C46.9128 19.8816 46.667 19.2881 46.667 18.6693V14.0026H9.33366V42.0026H28.0003C28.6192 42.0026 29.2127 42.2484 29.6502 42.686C30.0878 43.1236 30.3337 43.7171 30.3337 44.3359C30.3337 44.9548 30.0878 45.5483 29.6502 45.9859C29.2127 46.4234 28.6192 46.6693 28.0003 46.6693H9.33366C8.09598 46.6693 6.909 46.1776 6.03383 45.3024C5.15866 44.4273 4.66699 43.2403 4.66699 42.0026V14.0026C4.66699 12.7649 5.15866 11.5779 6.03383 10.7028C6.909 9.8276 8.09598 9.33594 9.33366 9.33594H46.667ZM44.3337 41.7086C42.8032 42.1038 41.1974 42.1038 39.667 41.7086V45.2273L40.9573 44.5833C41.2812 44.4214 41.6383 44.3372 42.0003 44.3372C42.3624 44.3372 42.7195 44.4214 43.0433 44.5833L44.3337 45.2273V41.7086ZM42.0003 28.0026C40.7626 28.0026 39.5757 28.4943 38.7005 29.3694C37.8253 30.2446 37.3337 31.4316 37.3337 32.6693C37.3337 33.9069 37.8253 35.0939 38.7005 35.9691C39.5757 36.8443 40.7626 37.3359 42.0003 37.3359C43.238 37.3359 44.425 36.8443 45.3002 35.9691C46.1753 35.0939 46.667 33.9069 46.667 32.6693C46.667 31.4316 46.1753 30.2446 45.3002 29.3694C44.425 28.4943 43.238 28.0026 42.0003 28.0026ZM21.0003 30.3359C21.595 30.3366 22.1671 30.5643 22.5995 30.9726C23.032 31.3808 23.2922 31.9388 23.3271 32.5325C23.3619 33.1262 23.1688 33.7108 22.787 34.1669C22.4053 34.6229 21.8639 34.916 21.2733 34.9863L21.0003 35.0026H16.3337C15.7389 35.0019 15.1669 34.7742 14.7345 34.366C14.302 33.9577 14.0418 33.3997 14.0069 32.806C13.9721 32.2123 14.1652 31.6277 14.5469 31.1717C14.9287 30.7156 15.4701 30.4225 16.0607 30.3523L16.3337 30.3359H21.0003ZM30.3337 21.0026C30.9525 21.0026 31.546 21.2484 31.9836 21.686C32.4212 22.1236 32.667 22.7171 32.667 23.3359C32.667 23.9548 32.4212 24.5483 31.9836 24.9859C31.546 25.4234 30.9525 25.6693 30.3337 25.6693H16.3337C15.7148 25.6693 15.1213 25.4234 14.6837 24.9859C14.2462 24.5483 14.0003 23.9548 14.0003 23.3359C14.0003 22.7171 14.2462 22.1236 14.6837 21.686C15.1213 21.2484 15.7148 21.0026 16.3337 21.0026H30.3337Z" fill="#929292"/>
                </svg>
              </UploadIcon>
              <UploadText $disabled>{t('page.myAccount.upload.idCopy')}</UploadText>
              <UploadButton $disabled>{t('common.actions.selectFile')}</UploadButton>
            </>
          ) : idFile ? (
            <ImagePreviewOverlay $imageUrl={idFile}>
              <div style={{ zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: convertToVwWeb(16) }}>
                <UploadIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none" style={{ width: '100%', height: '100%' }}>
                    <path d="M41.9993 23.3359C43.7956 23.3358 45.5538 23.8541 47.0628 24.8285C48.5718 25.8029 49.7676 27.1921 50.5067 28.8293C51.2457 30.4665 51.4966 32.2822 51.2292 34.0585C50.9619 35.8348 50.1876 37.4962 48.9993 38.8433V48.6246C48.9994 49.0622 48.8876 49.4925 48.6746 49.8747C48.4615 50.2568 48.1543 50.5782 47.7821 50.8083C47.41 51.0383 46.9851 51.1694 46.548 51.1891C46.1109 51.2087 45.676 51.1163 45.2847 50.9206L41.9993 49.2779L38.714 50.9206C38.3227 51.1163 37.8878 51.2087 37.4507 51.1891C37.0136 51.1694 36.5887 51.0383 36.2165 50.8083C35.8444 50.5782 35.5372 50.2568 35.3241 49.8747C35.1111 49.4925 34.9993 49.0622 34.9993 48.6246V38.8433C33.8111 37.4962 33.0368 35.8348 32.7695 34.0585C32.5021 32.2822 32.753 30.4665 33.492 28.8293C34.2311 27.1921 35.4269 25.8029 36.9359 24.8285C38.4449 23.8541 40.2031 23.3358 41.9993 23.3359ZM46.666 9.33594C47.9037 9.33594 49.0907 9.8276 49.9658 10.7028C50.841 11.5779 51.3327 12.7649 51.3327 14.0026V18.6693C51.3327 19.2881 51.0868 19.8816 50.6493 20.3192C50.2117 20.7568 49.6182 21.0026 48.9993 21.0026C48.3805 21.0026 47.787 20.7568 47.3494 20.3192C46.9118 19.8816 46.666 19.2881 46.666 18.6693V14.0026H9.33268V42.0026H27.9993C28.6182 42.0026 29.2117 42.2484 29.6493 42.686C30.0868 43.1236 30.3327 43.7171 30.3327 44.3359C30.3327 44.9548 30.0868 45.5483 29.6493 45.9859C29.2117 46.4234 28.6182 46.6693 27.9993 46.6693H9.33268C8.09501 46.6693 6.90802 46.1776 6.03285 45.3024C5.15768 44.4273 4.66602 43.2403 4.66602 42.0026V14.0026C4.66602 12.7649 5.15768 11.5779 6.03285 10.7028C6.90802 9.8276 8.09501 9.33594 9.33268 9.33594H46.666ZM44.3327 41.7086C42.8022 42.1038 41.1965 42.1038 39.666 41.7086V45.2273L40.9563 44.5833C41.2802 44.4214 41.6373 44.3372 41.9993 44.3372C42.3614 44.3372 42.7185 44.4214 43.0423 44.5833L44.3327 45.2273V41.7086ZM41.9993 28.0026C40.7617 28.0026 39.5747 28.4943 38.6995 29.3694C37.8243 30.2446 37.3327 31.4316 37.3327 32.6693C37.3327 33.9069 37.8243 35.0939 38.6995 35.9691C39.5747 36.8443 40.7617 37.3359 41.9993 37.3359C43.237 37.3359 44.424 36.8443 45.2992 35.9691C46.1743 35.0939 46.666 33.9069 46.666 32.6693C46.666 31.4316 46.1743 30.2446 45.2992 29.3694C44.424 28.4943 43.237 28.0026 41.9993 28.0026ZM20.9993 30.3359C21.5941 30.3366 22.1661 30.5643 22.5985 30.9726C23.031 31.3808 23.2912 31.9388 23.3261 32.5325C23.3609 33.1262 23.1678 33.7108 22.7861 34.1669C22.4044 34.6229 21.8629 34.916 21.2723 34.9863L20.9993 35.0026H16.3327C15.738 35.0019 15.1659 34.7742 14.7335 34.366C14.301 33.9577 14.0408 33.3997 14.0059 32.806C13.9711 32.2123 14.1643 31.6277 14.546 31.1717C14.9277 30.7156 15.4691 30.4225 16.0597 30.3523L16.3327 30.3359H20.9993ZM30.3327 21.0026C30.9515 21.0026 31.545 21.2484 31.9826 21.686C32.4202 22.1236 32.666 22.7171 32.666 23.3359C32.666 23.9548 32.4202 24.5483 31.9826 24.9859C31.545 25.4234 30.9515 25.6693 30.3327 25.6693H16.3327C15.7138 25.6693 15.1204 25.4234 14.6828 24.9859C14.2452 24.5483 13.9993 23.9548 13.9993 23.3359C13.9993 22.7171 14.2452 22.1236 14.6828 21.686C15.1204 21.2484 15.7138 21.0026 16.3327 21.0026H30.3327Z" fill="white"/>
                  </svg>
                </UploadIcon>
                <UploadText style={{ color: theme.colors.grayScale[0] }}>{isIndividual ? t('page.myAccount.upload.residentIdCopy') : t('page.myAccount.upload.idCopy')}</UploadText>
                <UploadButton onClick={(e) => {
                  e.stopPropagation();
                  document.getElementById('id-upload')?.click();
                }}>{t('common.actions.selectFile')}</UploadButton>
              </div>
            </ImagePreviewOverlay>
          ) : (
            <>
              <UploadIcon>
                <img src={certificateIcon} alt="upload" style={{ width: '100%' }} />
              </UploadIcon>
              <UploadText>{isIndividual ? t('page.myAccount.upload.residentIdCopy') : t('page.myAccount.upload.idCopy')}</UploadText>
              <UploadButton>{t('common.actions.selectFile')}</UploadButton>
            </>
          )}
        </UploadBox>
        {showErrors && errors.hasIdCopy && <ErrorText style={{ position: 'static' }}>{t('common.errors.required')}</ErrorText>}
      </div>
      ) : null}
    </>
    );
  };

  const renderTipBelowCard = () => {
    const wrap = (inner: React.ReactNode) => <TipBelowCardOuter>{inner}</TipBelowCardOuter>;

    if (activeTab === 'basic') {
      return wrap(
        <TipBox>
          <TipIcon>
            <img src={hugeIcon} alt="" />
          </TipIcon>
          <TipText dangerouslySetInnerHTML={{ __html: (t('page.myAccount.tips.default') as string).replace(/\n/g, '<br />') }} />
        </TipBox>,
      );
    }
    if (activeTab === 'ministry') {
      return wrap(
        <TipBox>
          <TipIcon>
            <img src={hugeIcon} alt="" />
          </TipIcon>
          <TipText dangerouslySetInnerHTML={{ __html: (t('page.myAccount.tips.security') as string).replace(/\n/g, '<br />') }} />
        </TipBox>,
      );
    }
    if (activeTab === 'bank') {
      if (isStepMode) return null;
      return wrap(
        <TipBox>
          <TipIcon>
            <img src={hugeIcon} alt="" />
          </TipIcon>
          <TipText dangerouslySetInnerHTML={{ __html: (t('page.myAccount.tips.default') as string).replace(/\n/g, '<br />') }} />
        </TipBox>,
      );
    }
    if (activeTab === 'verification') {
      if (!isIndividualUserType(userProfile?.user_type)) return null;
      const verificationTipKey = verificationStatus === 'none'
        ? 'page.myAccount.tips.verificationInitial'
        : 'page.myAccount.tips.verification';
      return wrap(
        <TipBox>
          <TipIcon>
            <img src={hugeIcon} alt="" />
          </TipIcon>
          <TipText
            dangerouslySetInnerHTML={{
              __html: (t(verificationTipKey) as string).replace(/\n/g, '<br />'),
            }}
          />
        </TipBox>,
      );
    }
    return null;
  };

  return (
    <WebPageLayout title={t('page.myAccount.pageTitle')}>
      <PageContainer>
        <ContentWrapper>
          {isStepMode && (
            <>
              {renderStepIndicator()}
            </>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwWeb(11), width: '100%' }}>
            {!isStepMode && (
              <TabMenu>
                <TabItem active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
                  <img
                    src={activeTab === 'basic' ? searchColorIcon : searchIcon}
                    alt="basic"
                    style={{ width: convertToVwWeb(22), height: convertToVwWeb(22) }}
                  />
                  <p>{t('page.myAccount.tabs.basic')}</p>
                </TabItem>
                <TabItem active={activeTab === 'ministry'} onClick={() => setActiveTab('ministry')}>
                  <img src={activeTab === 'ministry' ? buildingColorIcon : buildingIcon} alt="ministry" style={{ filter: activeTab === 'ministry' ? 'none' : 'grayscale(1) opacity(0.5)' }} />
                  <p>{t('page.myAccount.tabs.ministry')}</p>
                </TabItem>
                <TabItem active={activeTab === 'bank'} onClick={() => setActiveTab('bank')}>
                  <img src={activeTab === 'bank' ? bankColorIcon : bankIcon} alt="bank" style={{ filter: activeTab === 'bank' ? 'none' : 'grayscale(1) opacity(0.5)' }} />
                  <p>{t('page.myAccount.tabs.bank')}</p>
                </TabItem>
                <TabItem active={activeTab === 'verification'} onClick={() => setActiveTab('verification')}>
                  <img src={activeTab === 'verification' ? securityColorIcon : securityIcon} alt="verification" style={{ filter: activeTab === 'verification' ? 'none' : 'grayscale(1) opacity(0.5)' }} />
                  <p>{t('page.myAccount.tabs.verification')}</p>
                </TabItem>
              </TabMenu>
            )}
            <CardWithButtonsWrapper>
              <FormCardWithFooter>
                <SectionContainer>
                  {activeTab === 'basic' && renderBasicInfo()}
                  {activeTab === 'ministry' && renderMinistryInfo()}
                  {activeTab === 'bank' && renderBankInfo()}
                  {activeTab === 'verification' && renderVerification()}
                </SectionContainer>
                <ButtonGroup>
                  {isStepMode ? (
                    <CancelSaveButtons
                      onCancel={
                        activeTab === 'basic'
                          ? () => {
                              pendingCancelHandlerRef.current = handleClearAll;
                              handleRequestCancel();
                            }
                          : handlePrev
                      }
                      onSave={handleNext}
                      cancelLabel={activeTab === 'basic' ? t('common.actions.cancel') : t('common.actions.previous')}
                      saveLabel={activeTab === 'verification' ? t('common.actions.save') : t('common.actions.next')}
                      saving={isSaving}
                      savingLabel={t('common.actions.saving')}
                      disabled={showErrors && Object.values(errors).some(Boolean)}
                      platform="web"
                    />
                  ) : (
                    <CancelSaveButtons
                      onCancel={() => {
                        pendingCancelHandlerRef.current = handleCancel;
                        if (isDirty) {
                          setIsCancelConfirmOpen(true);
                        } else {
                          handleCancel();
                        }
                      }}
                      onSave={handleSave}
                      saving={isSaving}
                      savingLabel={t('common.actions.saving')}
                      platform="web"
                    />
                  )}
                </ButtonGroup>
              </FormCardWithFooter>
              {renderTipBelowCard()}
            </CardWithButtonsWrapper>
          </div>
        </ContentWrapper>
      </PageContainer>

      {isSubmitModalOpen && (
        <ModalOverlay onClick={() => setIsSubmitModalOpen(false)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalTitle>{t('page.myAccount.modal.submitTitle')}</ModalTitle>
            <ModalDesc>
              <p>{t('page.myAccount.modal.submitLine1')}</p>
              <p>{t('page.myAccount.modal.submitLine2')}</p>
            </ModalDesc>
            <ModalButtonGroup>
              <ModalButton onClick={() => navigate(PATHS.PROJECT_MANAGEMENT)}>{t('common.actions.confirm')}</ModalButton>
              <ModalButton primary onClick={() => navigate(PATHS.NEW_PROJECT)}>{t('page.myAccount.modal.createProject')}</ModalButton>
            </ModalButtonGroup>
          </ModalContainer>
        </ModalOverlay>
      )}

      <DSModal
        open={isCancelConfirmOpen}
        size="compact"
        title={t('page.myAccount.modal.cancelConfirmTitle')}
        description={t('page.myAccount.modal.cancelConfirmDesc')}
        onClose={() => setIsCancelConfirmOpen(false)}
        actions={[
          { label: t('page.myAccount.modal.cancelConfirm'), variant: 'secondary', onClick: handleConfirmCancel },
          { label: t('page.myAccount.modal.continueInput'), variant: 'primary', onClick: () => setIsCancelConfirmOpen(false) },
        ]}
      />
    </WebPageLayout>
  );
};

export default MyAccountWeb;
